/**
 * useDataGrid — Orchestrator Hook
 *
 * Composes selection + keyboard + editing + mutation + undo + clipboard
 * into a single API. This is the only hook consumers need to import.
 *
 * Epic 01: selection + keyboard navigation
 * Epic 02: editing + mutation bridge
 * Epic 03: undo/redo + clipboard
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useDataGridSelection } from './useDataGridSelection';
import { useDataGridKeyboard } from './useDataGridKeyboard';
import { useDataGridEditing } from './useDataGridEditing';
import { useDataGridMutation } from './useDataGridMutation';
import { useDataGridUndo } from './useDataGridUndo';
import { useDataGridClipboard } from './useDataGridClipboard';
import { useDataGridRangeSelection } from './useDataGridRangeSelection';
import { useDataGridMouseDrag } from './useDataGridMouseDrag';
import type { DataGridOptions, UndoEntry, UseDataGridReturn } from './types';

// No-op keyboard handler for when edit mode is off
const NOOP_KEYDOWN = () => {};

export function useDataGrid(opts: DataGridOptions): UseDataGridReturn {
  // Range selection — instantiate before selection so callback is ready
  const rangeSelection = useDataGridRangeSelection({
    visibleColumnIds: opts.visibleColumnIds,
  });

  // Keep anchor in a ref so the drag hook always reads the latest value
  const anchorRef = useRef(rangeSelection.anchor);
  anchorRef.current = rangeSelection.anchor;

  const selection = useDataGridSelection({
    editableFields: opts.editableFields,
    visibleColumnIds: opts.visibleColumnIds,
    rowCount: opts.rowCount,
    onCellSelected: (pos) => rangeSelection.setRange(pos, pos),
  });

  const mouseDrag = useDataGridMouseDrag({
    isEditMode: opts.isEditMode,
    setRange: rangeSelection.setRange,
    selectCell: selection.selectCell,
    scrollContainerRef: opts.scrollContainerRef,
    anchorRef,
  });

  const mutation = useDataGridMutation(opts.doctype);

  const editing = useDataGridEditing({
    activeCell: selection.activeCell,
    getRowData: opts.getRowData,
    doctype: opts.doctype,
    saveCell: mutation.saveCell,
  });

  // Shared isPastingRef — breaks the circular dep between clipboard and undo.
  // Clipboard sets it; undo reads it.
  const isPastingRef = useRef(false);

  // Stable ref for pushEntry — clipboard uses this, populated after undo hook
  const pushEntryRef = useRef<(entry: UndoEntry) => void>(() => {});

  const pushUndoEntry = useCallback(
    (entry: UndoEntry) => pushEntryRef.current(entry),
    [],
  );

  const getFieldLabel = useCallback(
    (fieldname: string) => opts.getFieldLabel?.(fieldname) ?? fieldname,
    [opts.getFieldLabel],
  );

  // Memoize filtered column list (excludes __select) for range operations
  const filteredVisibleColumnIds = useMemo(
    () => opts.visibleColumnIds.filter((id) => id !== '__select'),
    [opts.visibleColumnIds],
  );

  const clipboard = useDataGridClipboard({
    activeCell: selection.activeCell,
    getRowData: opts.getRowData,
    saveCell: mutation.saveCell,
    pushUndoEntry,
    doctype: opts.doctype,
    rowCount: opts.rowCount,
    getFieldLabel,
    isPastingRef,
    selectionRange: rangeSelection.selectionRange,
    normalizedRect: rangeSelection.normalizedRect,
    visibleColumnIds: filteredVisibleColumnIds,
    editableColumnIds: selection.editableColumnIds,
  });

  const undo = useDataGridUndo({
    saveCell: mutation.saveCell,
    isPastingRef,
  });

  // Wire up the ref now that undo exists
  pushEntryRef.current = undo.pushEntry;

  // Wrap commitEdit to also push to undo stack
  const commitEditWithUndo = useCallback(
    (value: unknown) => {
      if (!editing.isCellEditing || !selection.activeCell) return;
      const row = opts.getRowData(selection.activeCell.rowIndex);
      if (!row) return;
      const oldValue = row[selection.activeCell.columnId];
      if (value === oldValue) {
        editing.commitEdit(value);
        return;
      }

      // Push to undo before committing (commit is fire-and-forget)
      undo.pushEntry({
        doctype: opts.doctype,
        name: row.name as string,
        fieldname: selection.activeCell.columnId,
        oldValue,
        newValue: value,
        timestamp: Date.now(),
      });

      editing.commitEdit(value);
    },
    [editing, selection.activeCell, opts, undo],
  );

  // Wrap clearSelection to also clear range
  const clearSelectionAndRange = useCallback(() => {
    selection.clearSelection();
    rangeSelection.clearRange();
  }, [selection.clearSelection, rangeSelection.clearRange]);

  // Keyboard help popover state
  const [isHelpOpen, setHelpOpen] = useState(false);

  // Clear undo stack when edit mode is turned off
  const prevEditMode = useRef(opts.isEditMode);
  useEffect(() => {
    if (prevEditMode.current && !opts.isEditMode) {
      undo.clearStack();
    }
    prevEditMode.current = opts.isEditMode;
  }, [opts.isEditMode, undo]);

  // Range delete: clear all editable cells in the selected range
  const handleRangeDelete = useCallback(async () => {
    const rect = rangeSelection.normalizedRect;
    if (!rect) return;
    const batchId = Date.now().toString(36);
    let count = 0;

    for (let r = rect.minRow; r <= rect.maxRow; r++) {
      const row = opts.getRowData(r);
      if (!row) continue;
      const name = row.name as string;
      if (!name) continue;
      for (let c = rect.minColIdx; c <= rect.maxColIdx; c++) {
        const colId = filteredVisibleColumnIds[c];
        if (!selection.editableColumnIds.includes(colId)) continue;
        const oldValue = row[colId];
        const success = await mutation.saveCell({
          doctype: opts.doctype,
          name,
          fieldname: colId,
          value: '',
          oldValue,
        });
        if (success) {
          undo.pushEntry({
            doctype: opts.doctype,
            name,
            fieldname: colId,
            oldValue,
            newValue: '',
            timestamp: Date.now(),
            batchId,
          });
          count++;
        }
      }
    }

    if (count > 0) {
      toast(`Cleared ${count} cells`, {
        action: { label: 'Undo', onClick: () => undo.undo() },
      });
    }
  }, [rangeSelection.normalizedRect, opts, filteredVisibleColumnIds, selection.editableColumnIds, mutation, undo]);

  const keyboard = useDataGridKeyboard({
    activeCell: selection.activeCell,
    selectCell: selection.selectCell,
    clearSelection: clearSelectionAndRange,
    getNextEditableCell: selection.getNextEditableCell,
    getPrevEditableCell: selection.getPrevEditableCell,
    getCellBelow: selection.getCellBelow,
    getCellAbove: selection.getCellAbove,
    getFirstInRow: selection.getFirstInRow,
    getLastInRow: selection.getLastInRow,
    getFirstCellInTable: selection.getFirstCellInTable,
    getLastCellInTable: selection.getLastCellInTable,
    getCellPageDown: selection.getCellPageDown,
    getCellPageUp: selection.getCellPageUp,
    isEditMode: opts.isEditMode,
    isCellEditing: editing.isCellEditing,
    onCommitAndMove: () => {
      if (editing.isCellEditing) {
        commitEditWithUndo(editing.editingValue);
      }
    },
    onCancelEdit: editing.cancelEdit,
    // Undo/redo/clipboard shortcuts
    onUndo: undo.undo,
    onRedo: undo.redo,
    onCopy: clipboard.copy,
    onCut: clipboard.cut,
    onPaste: clipboard.paste,
    onShowHelp: () => setHelpOpen(true),
    onDelete: handleRangeDelete,
    // Range selection (Plan 124, Epic 03)
    selectionRange: rangeSelection.selectionRange,
    setRange: rangeSelection.setRange,
    clearRange: rangeSelection.clearRange,
    visibleColumnIds: filteredVisibleColumnIds,
    rowCount: opts.rowCount,
    editableColumnIds: selection.editableColumnIds,
    scrollContainerRef: opts.scrollContainerRef,
  });

  const isCellActive = useCallback(
    (rowIndex: number, columnId: string) => {
      return selection.isCellActive(rowIndex, columnId);
    },
    [selection.isCellActive],
  );

  return {
    activeCell: selection.activeCell,
    selectCell: selection.selectCell,
    clearSelection: clearSelectionAndRange,
    isCellActive,
    isCellEditable: selection.isCellEditable,
    handleKeyDown: opts.isEditMode ? keyboard.handleKeyDown : NOOP_KEYDOWN,
    // Editing
    isCellEditing: editing.isCellEditing,
    startEditing: editing.startEditing,
    commitEdit: commitEditWithUndo,
    cancelEdit: editing.cancelEdit,
    editingValue: editing.editingValue,
    // Mutation state
    getCellSaveState: mutation.getCellSaveState,
    getRowSaveState: mutation.getRowSaveState,
    // Undo/redo
    undo: undo.undo,
    redo: undo.redo,
    canUndo: undo.canUndo,
    canRedo: undo.canRedo,
    // Clipboard
    copy: clipboard.copy,
    cut: clipboard.cut,
    paste: clipboard.paste,
    pastePreview: clipboard.pastePreview,
    isPasting: clipboard.isPasting,
    applyPaste: clipboard.applyPaste,
    cancelPaste: clipboard.cancelPaste,
    pasteProgress: clipboard.pasteProgress,
    copyFlashActive: clipboard.copyFlashActive,
    // Range selection (Plan 124)
    selectionRange: rangeSelection.selectionRange,
    isCellInRange: rangeSelection.isCellInRange,
    getCellRangePosition: rangeSelection.getCellRangePosition,
    rangeSize: rangeSelection.rangeSize,
    setRange: rangeSelection.setRange,
    clearRange: rangeSelection.clearRange,
    // Mouse drag (Plan 124, Epic 02)
    handleCellMouseDown: mouseDrag.handleCellMouseDown,
    isDragging: mouseDrag.isDragging,
    // Help
    isHelpOpen,
    setHelpOpen,
  };
}
